import {throwError, merge, fromEvent, BehaviorSubject, Subject} from 'rxjs';

import {catchError, debounceTime, map, takeUntil} from 'rxjs/operators';
import {
  Directive,
  ElementRef,
  EmbeddedViewRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef
} from '@angular/core';
import {PopupRef, PopupService} from '@progress/kendo-angular-popup';
import {ScreenshotConfig} from '../../../shared/ui/screenshot-popover/models/screenshot-config';
import {ScreenshotPopoverComponent} from 'src/app/shared/ui/screenshot-popover/screenshot-popover.component';
import {ResizeBatchService} from '@progress/kendo-angular-common';

@Directive({
  selector: '[q9Screenshot]',
  providers: [PopupService, ResizeBatchService],
  standalone: true
})
export class ScreenshotPopoverDirective implements OnInit, OnDestroy, OnChanges {
  private innerView: EmbeddedViewRef<any>;
  private popupRef: PopupRef;
  private mouseState: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private destroyed = new Subject();
  private oldId: string;
  private canSpawn = true;

  @Input('q9Screenshot') private config: ScreenshotConfig | any;

  constructor(
    private tRef: TemplateRef<Comment>,
    private vcRef: ViewContainerRef,
    private popupService: PopupService
  ) {}

  ngOnInit() {
    this.renderView();
    this.bindMouseEvents();
    this.mouseState
      .pipe(takeUntil(this.destroyed))
      .subscribe(over => (over ? this.openPopup() : this.closePopup()));
  }

  ngOnDestroy() {
    this.closePopup();
    this.canSpawn = false; //sometimes "mouseState" subscription handlers fire after component destruction
    this.destroyed.next(void 0);
    this.destroyed.complete();
  }

  ngOnChanges() {
    if (!this.config || !this.config.id) {
      this.closePopup();
    }

    if (this.config && this.oldId && this.oldId !== this.config.id) {
      this.closePopup();
    }
  }

  private renderView() {
    this.innerView = this.vcRef.createEmbeddedView(this.tRef);
  }

  private openPopup() {
    if (!this.canSpawn || this.popupRef || !this.config || !this.config.id) {
      return;
    }

    this.canSpawn = false;
    const anchorAlign = {horizontal: 'center', vertical: 'bottom'};
    const popupAlign = {horizontal: 'center', vertical: 'top'};
    const collision = {horizontal: 'fit', vertical: 'flip'};

    this.popupRef = this.popupService.open({
      anchor: this.anchor(),
      animate: false,
      positionMode: 'absolute',
      anchorAlign: this.config.anchorAlign || anchorAlign,
      popupAlign: this.config.popupAlign || popupAlign,
      content: ScreenshotPopoverComponent,
      collision: this.config.collision || collision,
      popupClass: ['q9-screenshot-popup']
    });

    if (this.popupRef.popupElement) {
      this.popupRef.popupElement.style.position = 'absolute';
      this.popupListeners.add(this.popupRef.popupElement);
    }

    if ((this.popupRef.content || ({} as any)).instance) {
      this.popupRef.content.instance.injectConfig(this.config);
      this.oldId = this.config.id;
    }
  }

  private closePopup() {
    if (this.popupRef) {
      this.popupListeners.remove(this.popupRef.popupElement);
      this.popupRef.close();
      this.popupRef = null;
    }

    this.canSpawn = true;
  }

  private get popupListeners() {
    const forceClosePopup = () => this.closePopup();

    return {
      add: (elem: HTMLElement) => {
        elem.addEventListener('click', forceClosePopup);
        elem.addEventListener('mouseover', forceClosePopup);
      },
      remove: (elem: HTMLElement) => {
        elem.removeEventListener('click', forceClosePopup);
        elem.removeEventListener('mouseover', forceClosePopup);
      }
    };
  }

  private anchor() {
    return new ElementRef<HTMLElement>(this.innerView.rootNodes[0]);
  }

  private bindMouseEvents() {
    const mouseEnter = fromEvent(this.innerView.rootNodes[0], 'mouseover').pipe(
      takeUntil(this.destroyed),
      map(e => true)
    );
    const mouseLeave = fromEvent(this.innerView.rootNodes[0], 'mouseout').pipe(
      takeUntil(this.destroyed),
      map(e => false)
    );

    merge(mouseEnter, mouseLeave)
      .pipe(
        takeUntil(this.destroyed),
        debounceTime(400),
        catchError(err => {
          console.warn('popup error', err);
          this.closePopup();

          return throwError(err);
        })
      )
      .subscribe(this.mouseState);
  }
}
